import BusinessIcon from '@mui/icons-material/Business';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import PieChartIcon from '@mui/icons-material/PieChart';
import { Stack, Typography } from '@mui/material';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { ResultCSV } from './project-result-preview';

interface Props {
  resultObject: ResultCSV;
  isEven: boolean;
}

const ProjectResultRow: React.FC<Props> = ({ resultObject, isEven }) => {
  const { classes, cx } = useStyles({ isEven });

  return (
    <div className={classes.resultRow}>
      <Stack direction={'column'} gap={1}>
        <Breadcrumbs className={classes.breadcrumbs}>
          {!_.isNil(resultObject.firstname) || !_.isNil(resultObject.lastname) ? (
            <div className={classes.flexContainer}>
              <PersonIcon className={classes.icon} />
              {!_.isNil(resultObject.firstname) && !_.isNil(resultObject.lastname) ? (
                <Typography className={classes.keyword}>{`${resultObject.firstname} ${resultObject.lastname}`}</Typography>
              ) : !_.isNil(resultObject.firstname) ? (
                <Typography className={classes.keyword}>{resultObject.firstname}</Typography>
              ) : !_.isNil(resultObject.lastname) ? (
                <Typography className={classes.keyword}>{resultObject.lastname}</Typography>
              ) : null}
            </div>
          ) : null}
          {!_.isNil(resultObject.companyName) ? (
            <div className={classes.flexContainer}>
              <BusinessIcon className={classes.icon} />
              <Typography className={classes.keyword}>{resultObject.companyName}</Typography>
            </div>
          ) : null}
          {!_.isNil(resultObject.type) ? (
            <div className={classes.flexContainer}>
              <PieChartIcon className={cx(classes.icon)} />
              <Typography className={cx(classes.keyword)}>{resultObject.type}</Typography>
            </div>
          ) : null}
        </Breadcrumbs>
        <Stack direction={'row'} alignItems={'center'}>
          <LinkIcon className={classes.icon} />
          <PaiLink
            text={_.truncate(resultObject.url, { length: 100 })}
            size={'sm'}
            variant={'primary'}
            underline={'always'}
            href={resultObject.url}
            target={'_blank'}
          />
          <OpenInNewIcon className={cx(classes.icon, 'right-placement', 'dense')} />
        </Stack>
      </Stack>
    </div>
  );
};

const useStyles = makeStyles<{ isEven: boolean }>()((theme, { isEven }) => ({
  resultRow: {
    position: 'relative',
    width: '100%',
    padding: '8px',
    background: isEven ? '#FFF' : theme.palette.backgrounds.main,
  },
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  breadcrumbs: {
    width: '100%',
    '& ol': {
      flexWrap: 'wrap',
      gap: '8px',
    },
    '& .MuiBreadcrumbs-separator': {
      color: theme.palette.greys.medium,
      height: theme.typography.pxToRem(20),
      [theme.breakpoints.down(400)]: {
        height: theme.typography.pxToRem(16),
      },
    },
  },
  keyword: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.greys.medium,
    lineHeight: 1,
    marginTop: '4px',
    [theme.breakpoints.down(400)]: {
      fontSize: theme.typography.pxToRem(10),
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
    marginRight: '8px',
    color: theme.palette.greys.medium,
    '&.right-placement': {
      color: theme.palette.greys.medium,
      marginRight: 0,
      marginLeft: '8px',
    },
    '&.dense': {
      fontSize: theme.typography.pxToRem(16),
    },
    [theme.breakpoints.down(400)]: {
      fontSize: theme.typography.pxToRem(16),
      '&.dense': {
        fontSize: theme.typography.pxToRem(12),
      },
    },
  },
}));

export default ProjectResultRow;
